import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PAYMENT_METHODS, type PaymentMethod } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import { formatCurrency, formatDate } from '../lib/format.js';
import { useSettleVendor, useVendorStatement } from '../lib/outsourcing/use-outsourcing.js';

/**
 * One vendor's account: what has come back and been accepted, what is being withheld, what has
 * been advanced, and the one button that settles it.
 *
 * The arithmetic is shown as a sum rather than as a single figure — accepted work, less
 * deductions, less advances — because that is the conversation the shop is about to have with
 * the vendor, and an owner who cannot see the working cannot have it.
 *
 * It is the payout statement's layout on purpose. An owner who has settled a karigar this month
 * should not have to learn a second screen to settle an embroiderer.
 */
export function VendorPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { vendorId = '' } = useParams();
  const statement = useVendorStatement(vendorId);
  const settle = useSettleVendor(vendorId);
  const [method, setMethod] = useState<PaymentMethod>('upi');

  const data = statement.data;

  return (
    <>
      <PageHeader
        title={data?.vendor.name ?? t('outsourcing.title')}
        {...(data?.vendor.paymentTerms ? { description: data.vendor.paymentTerms } : {})}
        action={
          <Button asChild variant="ghost">
            <Link to="/outsourcing">
              <ArrowLeft aria-hidden="true" />
              {t('outsourcing.backToList')}
            </Link>
          </Button>
        }
      />

      {statement.isPending ? <ListSkeleton rows={4} /> : null}

      {data ? (
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('outsourcing.payable')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              {/* The working, not just the answer — this is the conversation about to happen. */}
              <Row label={t('outsourcing.acceptedWork')} value={data.unsettledPaise} />
              <Row label={t('outsourcing.deductions')} value={-data.pendingDeductionsPaise} />
              <Row label={t('outsourcing.advancesPaid')} value={-data.unappliedAdvancePaise} />
              <div className="border-t border-border pt-2">
                <Row label={t('outsourcing.netPayable')} value={data.netPayablePaise} strong />
              </div>

              {data.unsettled.length > 0 ? (
                <div className="flex flex-wrap items-end gap-3 pt-2">
                  <label className="flex flex-col gap-1 text-xs">
                    {t('outsourcing.method')}
                    <select
                      value={method}
                      aria-label={t('outsourcing.method')}
                      onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                      className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                    >
                      {PAYMENT_METHODS.map((one) => (
                        <option key={one} value={one}>
                          {t(`payments.method.${one}`, { defaultValue: one })}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button
                    disabled={settle.isPending}
                    onClick={() => {
                      void settle
                        .mutateAsync({
                          jobWorkIds: data.unsettled.map((job) => job.id),
                          method,
                        })
                        .then((result) => {
                          toast({
                            title: t('outsourcing.settled', {
                              amount: formatCurrency(result.netPaise),
                            }),
                          });
                        })
                        .catch(() => {
                          toast({ title: t('error.unexpected'), variant: 'destructive' });
                        });
                    }}
                  >
                    {t('outsourcing.settle')}
                  </Button>
                </div>
              ) : (
                <p className="pt-2 text-muted-foreground">{t('outsourcing.nothingToSettle')}</p>
              )}
            </CardContent>
          </Card>

          <section className="flex flex-col gap-2" aria-label={t('outsourcing.unsettledWork')}>
            <h2 className="text-sm font-semibold">{t('outsourcing.unsettledWork')}</h2>
            {data.unsettled.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('outsourcing.nothingBack')}</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {data.unsettled.map((job) => (
                  <li
                    key={job.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">{job.number}</span>
                      <span className="block text-muted-foreground">
                        {t(`outsourcing.workType.${job.workType}`)} ·{' '}
                        {t('outsourcing.acceptedCount', {
                          count: job.pieces.filter((piece) => piece.outcome === 'accepted').length,
                        })}
                        {job.pieces.some((piece) => piece.outcome === 'rejected')
                          ? ` · ${t('outsourcing.rejectedCount', {
                              count: job.pieces.filter((piece) => piece.outcome === 'rejected')
                                .length,
                            })}`
                          : ''}
                      </span>
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatCurrency(job.payablePaise)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="flex flex-col gap-2" aria-label={t('outsourcing.ledger')}>
            <h2 className="text-sm font-semibold">{t('outsourcing.ledger')}</h2>
            {data.ledger.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('outsourcing.noLedger')}</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {data.ledger.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">
                        {t(`outsourcing.ledgerKind.${entry.kind}`)}
                      </span>
                      <span className="block text-muted-foreground">
                        {formatDate(entry.occurredAt)}
                        {/* A deduction always carries its reason — it is the sentence the
                            argument is actually about. */}
                        {entry.reason ? ` · ${entry.reason}` : ''}
                        {entry.recordedByName ? ` · ${entry.recordedByName}` : ''}
                      </span>
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatCurrency(entry.amountPaise)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {data.settlements.length > 0 ? (
            <section className="flex flex-col gap-2" aria-label={t('outsourcing.settlements')}>
              <h2 className="text-sm font-semibold">{t('outsourcing.settlements')}</h2>
              <ul className="flex flex-col gap-1.5">
                {data.settlements.map((settlement) => (
                  <li
                    key={settlement.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">{settlement.number}</span>
                      <span className="block text-muted-foreground">
                        {formatDate(settlement.paidAt)} · {settlement.method}
                      </span>
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatCurrency(settlement.netPaise)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function Row({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: number;
  strong?: boolean;
}): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={strong ? 'font-semibold' : ''}>{label}</span>
      <span className={`tabular-nums ${strong ? 'font-semibold' : ''}`}>
        {value < 0 ? '−' : ''}
        {formatCurrency(Math.abs(value))}
      </span>
    </div>
  );
}

export default VendorPage;
